import type {
  ExperienceCategory,
  PostSupportTicketRequestBodyType,
} from "@intelligems/ig-types";
import {
  formatHumanizedDuration,
  officialCategoryName,
} from "@intelligems/ig-utils";

import { TEST_TYPE_OPTIONS } from "@src/features/support/pylon-support-form";
import { EXPERIMENT_ROUTES } from "@src/routes/routes";

import { EARLY_TEST_MIN_RUNTIME_DAYS } from "./earlyTestRuntime";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export type EarlyTestAction = "end" | "pause";

export interface EarlyTestTicketExperience {
  id: string;
  name: string;
  type: string;
  category: ExperienceCategory;
}

interface EarlyTestTicketOrg {
  name?: string | null;
  domain?: string | null;
  shopId?: string | null;
}

interface EarlyTestTicketUser {
  firstName?: string | null;
  lastName?: string | null;
  email: string;
}

interface BuildEarlyTestTicketArgs {
  experience: EarlyTestTicketExperience;
  org: EarlyTestTicketOrg;
  user: EarlyTestTicketUser;
  action: EarlyTestAction;
  /** Active runtime in days at the time of the early end/pause. */
  daysLive: number;
  /** window.location.origin, injected so the builder stays pure/testable. */
  origin: string;
  note?: string;
}

const ACTION_PAST_TENSE: Record<EarlyTestAction, string> = {
  end: "Ended",
  pause: "Paused",
};

/** Maps an experience type to the closest Pylon support-form test-type label. */
const experienceTypeToTestType = (type: string): string => {
  if (type === "pricing") return TEST_TYPE_OPTIONS.price_test;
  if (type === "shipping") return TEST_TYPE_OPTIONS.shipping_test;
  if (type === "personalization/offer") return TEST_TYPE_OPTIONS.offer_test;
  if (type === "checkoutBlock") return TEST_TYPE_OPTIONS.checkout_test;
  if (type.startsWith("content/")) return TEST_TYPE_OPTIONS.content_test;
  if (type.startsWith("personalization")) {
    return TEST_TYPE_OPTIONS.personalizations;
  }
  return TEST_TYPE_OPTIONS.no_test_type;
};

/**
 * Builds the pre-populated support-ticket body for a test ended or paused before
 * reaching statistical significance.
 *
 * The subject leads with the store name and the body packs the test ID, deep
 * link, store identifiers, and runtime context so Support can investigate even
 * if the customer later deletes the test.
 */
export const buildEarlyTestTicketPayload = ({
  experience,
  org,
  user,
  action,
  daysLive,
  origin,
  note,
}: BuildEarlyTestTicketArgs): PostSupportTicketRequestBodyType => {
  const storeName = org.name?.trim() || "Unknown store";

  const requesterName =
    [user.firstName, user.lastName]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(" ") || user.email;

  const testUrl = `${origin}${EXPERIMENT_ROUTES.Experiment}/${experience.id}`;

  const descriptionLines = [
    `${officialCategoryName(experience.category, true)} ID: ${experience.id}`,
    `${officialCategoryName(experience.category, true)} name: ${experience.name}`,
    `${officialCategoryName(experience.category, true)} link: ${testUrl}`,
    `Action taken: ${ACTION_PAST_TENSE[action]} before the recommended minimum runtime`,
    `Time live: ${formatHumanizedDuration(daysLive * MS_PER_DAY)} (recommended minimum ${EARLY_TEST_MIN_RUNTIME_DAYS} day${EARLY_TEST_MIN_RUNTIME_DAYS === 1 ? "" : "s"})`,
    `myShopify ID: ${org.shopId ?? "unknown"}`,
    `Storefront domain: ${org.domain ?? "unknown"}`,
  ];

  const trimmedNote = note?.trim();
  if (trimmedNote) {
    descriptionLines.push("", `Note from user: ${trimmedNote}`);
  }

  return {
    name: requesterName,
    email: user.email,
    subject: `[${storeName}] Early ${action} reported: "${experience.name}"`,
    shopifyUrl: org.shopId || "unknown",
    description: descriptionLines.join("\n"),
    testType: experienceTypeToTestType(experience.type),
    pageUrl: testUrl,
  };
};
